import { useRating } from '@hooks';
import { StoryToPlayFragment } from '@graphql/generated/graphql';
import { useRouter } from 'next/router';
import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
} from 'react';

interface RatingContext {
    isLiked: boolean;
    toggleLike: () => void;
}

interface Props extends PropsWithChildren {
    story: StoryToPlayFragment;
}

const RatingContext = createContext<RatingContext>({
    isLiked: false,
    toggleLike: () => {},
});

export const RatingProvider: FC<Props> = ({ children, story }) => {
    const {
        locale,
        query: { company, product },
    } = useRouter();

    const { rating, submitRating } = useRating({
        locale: locale as string,
        productSlug: product as string,
        storyTitle: story.Title,
        productUID: `${company}::${product}`,
    });

    const isLiked = useMemo(() => rating === 1, [rating]);

    const toggleLike = useCallback(() => {
        const newRating = rating === 1 ? 0 : 1;
        submitRating(newRating);
    }, [rating, submitRating]);

    return (
        <RatingContext.Provider value={{ isLiked, toggleLike }}>
            {children}
        </RatingContext.Provider>
    );
};

export const useRatingContext = () => useContext<RatingContext>(RatingContext);
